import { useEffect, useState } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useEditTourPlan } from '../hooks/useEditTourPlan'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import { Button, Box as DarkBox, DeleteIcon, TextInput } from '@hivewatch/dark'

import { UpdateTourPlanInput } from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'
import { getErrorMessage } from '@/utils/errorMessage'

import { CheckpointIProps } from '../../types/types'
import { areCheckpointsUpdated } from '../utils/utils'
import { DarkDeleteTourPlanModal } from './DarkDeleteTourPlanModal'
import { DarkFloorCheckpointGroups } from './DarkFloorCheckpointGroups'
import { DarkTourPlanFormHeader } from './DarkTourPlanFormHeader'
import {
  footerActionsGroupStyles,
  footerActionsStyles,
} from './DarkTourPlansPanel.styles'
import { DarkTourPlansSidePanel } from './DarkTourPlansSidePanel'

interface DarkEditTourPlanIProps {
  isLoading: boolean
  id: string
  name: string
  checkpoints: CheckpointIProps[]
  facilityId: string
  currentFloorId: string
  isExpanded: boolean
  onToggleExpansion: () => void
  onClose: () => void
  onUpdateTourPlan: (id: string) => void
  onDeleteTourPlan: () => void
}

export const DarkEditTourPlan = ({
  isLoading,
  id,
  name,
  checkpoints,
  facilityId,
  currentFloorId,
  isExpanded,
  onToggleExpansion,
  onClose,
  onUpdateTourPlan,
  onDeleteTourPlan,
}: DarkEditTourPlanIProps) => {
  const colorScheme = useLightMode()
  const { t } = useTranslation()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const me = useAppSelector(selectMe)
  const { shouldEnableDeleteTourPlan } = usePermissions()
  const { setHighlightedDeviceIds, setTourStopDeviceIds } = useFloorPlan()
  const { showSuccess, showError } = useToasts()
  const [planName, setPlanName] = useState(name ?? '')
  const {
    floorData,
    checkedIds,
    orginalCheckedIds,
    isUpdateLoading,
    handleChecked,
    update,
  } = useEditTourPlan(facilityId, checkpoints)

  useEffect(() => {
    setPlanName(name ?? '')
  }, [name])

  useEffect(() => {
    setTourStopDeviceIds(checkedIds)
  }, [checkedIds])

  const isNameValid = /\S/.test(planName)
  const hasChanges =
    planName !== name || areCheckpointsUpdated(orginalCheckedIds, checkedIds)

  const onSubmit = async () => {
    const input: UpdateTourPlanInput = {
      id,
      name: planName,
      checkpointIds: checkedIds,
    }
    trackEvent('Button Press: Tour Plan Details - Edit Tour Save', {
      facility_id: facilityId,
      tour_plan_id: id,
      role: me.roleName,
    })
    try {
      const data = await update(input)
      const updateTourPlanId = data?.updateTourPlan?.tourPlan?.id
      showSuccess(t('floors:plan_edit_tour_updated_success'))
      onUpdateTourPlan(updateTourPlanId)
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleOnClose = () => {
    setHighlightedDeviceIds(null)
    setTourStopDeviceIds(null)
    onClose()
  }

  const handleOnCancel = () => {
    trackEvent('Button Press: Tour Plan Details - Edit Tour Cancel', {
      facility_id: facilityId,
      tour_plan_id: id,
      role: me.roleName,
    })
    handleOnClose()
  }

  const handleDeleteModalOpen = () => {
    trackEvent('Button Press: Tour Plan Details - Delete Tour', {
      facility_id: facilityId,
      tour_plan_id: id,
      role: me.roleName,
    })
    setIsDeleteModalOpen(true)
  }

  const footer = (
    <DarkBox {...footerActionsStyles}>
      <Button
        colorScheme={colorScheme}
        data-testid='tourplan_editTourPlan_cancelButton'
        onClick={handleOnCancel}
        variant='tertiary'
      >
        {t('common:cancel')}
      </Button>
      <DarkBox {...footerActionsGroupStyles}>
        <Button
          colorScheme={colorScheme}
          data-testid='tourplan_editTourPlan_deleteButton'
          disabled={!shouldEnableDeleteTourPlan}
          leftIcon={<DeleteIcon size={14} />}
          onClick={handleDeleteModalOpen}
          variant='danger'
        >
          {t('common:delete')}
        </Button>
        <Button
          colorScheme={colorScheme}
          data-testid='tourplan_editTourPlan_saveButton'
          disabled={!isNameValid || !checkedIds?.length || !hasChanges}
          isLoading={isLoading || isUpdateLoading}
          onClick={onSubmit}
          variant='primary'
        >
          {t('common:save_changes')}
        </Button>
      </DarkBox>
    </DarkBox>
  )

  return (
    <>
      <DarkTourPlansSidePanel
        footer={footer}
        header={<DarkTourPlanFormHeader edit onClose={handleOnClose} />}
        isExpanded={isExpanded}
        onToggleExpansion={onToggleExpansion}
      >
        <TextInput
          colorScheme={colorScheme}
          data-testid='tourplan_editTourPlan_name'
          label={t('floors:plan_edit_tour_name_label')}
          onValueChange={setPlanName}
          placeholder={t('floors:plan_edit_tour_name_placeholder')}
          value={planName}
          width='100%'
        />
        <DarkFloorCheckpointGroups
          checkedIds={checkedIds}
          currentFloorId={currentFloorId}
          floorData={floorData}
          onChecked={handleChecked}
        />
      </DarkTourPlansSidePanel>

      <DarkDeleteTourPlanModal
        facilityId={facilityId}
        isOpen={isDeleteModalOpen}
        name={name}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={onDeleteTourPlan}
        tourPlanId={id}
      />
    </>
  )
}
